/**
 * adminExamPinStore — Exam Pins slice of the admin store (Zustand).
 * Exposes the exam-pins endpoints (`/api/admin/exam-pins`).
 *
 * Endpoints:
 *   GET    /api/admin/exam-pins              — list (page, limit, search, status, examType, sort)
 *   POST   /api/admin/exam-pins              — bulk create ({ pins: string[], examType })
 *   GET    /api/admin/exam-pins/:id          — single record detail
 *   GET    /api/admin/exam-pins/deliveries   — delivery log (page, limit, status)
 *   GET    /api/admin/exam-pins/stats        — summary statistics (optionally ?examType=)
 *   GET    /api/admin/exam-pins/price        — current price (optionally ?examType=)
 *   PATCH  /api/admin/exam-pins/price        — set price ({ price, examType })
 *   DELETE /api/admin/exam-pins/:id          — delete a pin
 */
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import { API_URL } from "../../constants";

export const examPinInitialState = {
  // List
  examPins: [],
  examPinsLoading: false,
  examPinsError: null,
  examPinTotal: 0,
  examPinPages: 1,

  // Single exam pin
  examPinDetail: null,
  examPinDetailLoading: false,
  examPinDetailError: null,

  // Deliveries
  examPinDeliveries: [],
  examPinDeliveriesLoading: false,
  examPinDeliveriesError: null,
  examPinDeliveryTotal: 0,
  examPinDeliveryPages: 1,

  // Stats
  examPinStats: null,
  examPinStatsLoading: false,
  examPinStatsError: null,

  // Price
  examPinPrice: null,
  examPinPriceLoading: false,
  examPinPriceError: null,

  // Mutation
  examPinMutationLoading: false,
  examPinMutationError: null,
};

/** Normalize any list payload shape into `{ items, total, totalPages }`. */
function normalizeListPayload(data) {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, totalPages: 1 };
  }
  return {
    items: payload.items || payload.examPins || [],
    total: payload.total ?? payload.totalItems ?? 0,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

function apiUrl(path) {
  if (!API_URL) {
    throw new Error("REACT_APP_API_BASE_URL is not configured.");
  }
  return `${API_URL.replace(/\/$/, "")}${path}`;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";

  if (!text) return {};
  if (contentType.includes("application/json")) return JSON.parse(text);
  if (text.trim().startsWith("<")) {
    const error = new Error(
      `Exam pins API returned HTML (${response.status}) for ${response.url}. ` +
      "Verify that the backend route is deployed and the API base URL is correct."
    );
    error.status = response.status;
    error.isHtmlResponse = true;
    throw error;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`API returned a non-JSON response for ${response.url}.`);
  }
}

async function fetchJson(path, options) {
  const response = await fetch(apiUrl(path), options);
  const data = await parseJsonResponse(response);
  return { response, data };
}

export function createExamPinActions(set, get) {
  return {
    // ── GET /api/admin/exam-pins ──
    fetchExamPins: async (params = {}) => {
      set({ examPinsLoading: true, examPinsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const { response, data } = await fetchJson(
          `/api/admin/exam-pins${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam pins");
          set({ examPinsLoading: false, examPinsError: error });
          return { success: false, error };
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({ examPinsLoading: false, examPins: items, examPinTotal: total, examPinPages: totalPages });
        return { success: true, examPins: items, total, pages: totalPages };
      } catch (err) {
        set({ examPinsLoading: false, examPinsError: err.message, examPins: [] });
        return { success: false, error: err.message };
      }
    },

    // ── POST /api/admin/exam-pins ──
    createExamPin: async (payload) => {
      set({ examPinMutationLoading: true, examPinMutationError: null });
      try {
        const { response, data } = await fetchJson(
          "/api/admin/exam-pins",
          {
            method: "POST",
            headers: authHeaders(resolveToken(get)),
            body: JSON.stringify(payload),
          }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to create exam pins");
          set({ examPinMutationLoading: false, examPinMutationError: error });
          return { success: false, error };
        }
        set({ examPinMutationLoading: false, successMessage: "Exam pins created" });
        return { success: true, examPin: data.data || data };
      } catch (err) {
        set({ examPinMutationLoading: false, examPinMutationError: err.message });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/exam-pins/:id ──
    fetchExamPinById: async (id) => {
      set({ examPinDetailLoading: true, examPinDetailError: null });
      try {
        const { response, data } = await fetchJson(
          `/api/admin/exam-pins/${id}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam pin");
          set({ examPinDetailLoading: false, examPinDetailError: error });
          return { success: false, error };
        }
        const detail = data?.data || data || null;
        set({ examPinDetailLoading: false, examPinDetail: detail });
        return { success: true, examPin: detail };
      } catch (err) {
        set({ examPinDetailLoading: false, examPinDetailError: err.message, examPinDetail: null });
        return { success: false, error: err.message };
      }
      },

    // ── GET /api/admin/exam-pins/deliveries ──
    fetchExamPinDeliveries: async (params = {}) => {
      set({ examPinDeliveriesLoading: true, examPinDeliveriesError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const { response, data } = await fetchJson(
          `/api/admin/exam-pins/deliveries${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam pin deliveries");
          set({ examPinDeliveriesLoading: false, examPinDeliveriesError: error });
          return { success: false, error };
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          examPinDeliveriesLoading: false,
          examPinDeliveries: items,
          examPinDeliveryTotal: total,
          examPinDeliveryPages: totalPages,
        });
        return { success: true, deliveries: items, total, pages: totalPages };
      } catch (err) {
        set({ examPinDeliveriesLoading: false, examPinDeliveriesError: err.message, examPinDeliveries: [] });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/exam-pins/stats ──
    fetchExamPinStats: async (params = {}) => {
      set({ examPinStatsLoading: true, examPinStatsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
        ).toString();
        const { response, data } = await fetchJson(
          `/api/admin/exam-pins/stats${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam pin stats");
          set({ examPinStatsLoading: false, examPinStatsError: error });
          return { success: false, error };
        }
        const stats = data?.data || data || null;
        set({ examPinStatsLoading: false, examPinStats: stats });
        return { success: true, stats };
      } catch (err) {
        set({ examPinStatsLoading: false, examPinStatsError: err.message, examPinStats: null });
        return { success: false, error: err.message };
      }
    },

    // ── DELETE /api/admin/exam-pins/:id ──
    deleteExamPin: async (id) => {
      set({ examPinMutationLoading: true, examPinMutationError: null });
      try {
        const { response, data } = await fetchJson(
          `/api/admin/exam-pins/${id}`,
          {
            method: "DELETE",
            headers: authHeaders(resolveToken(get)),
          }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to delete exam pin");
          set({ examPinMutationLoading: false, examPinMutationError: error });
          return { success: false, error };
        }
        set({ examPinMutationLoading: false, successMessage: "Exam pin deleted" });
        return { success: true };
      } catch (err) {
        set({ examPinMutationLoading: false, examPinMutationError: err.message });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/exam-pins/price ──
    fetchExamPinPrice: async (params = {}) => {
      set({ examPinPriceLoading: true, examPinPriceError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
        ).toString();
        const { response, data } = await fetchJson(
          `/api/admin/exam-pins/price${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch exam pin price");
          set({ examPinPriceLoading: false, examPinPriceError: error });
          return { success: false, error };
        }
        const payload = data?.data ?? data ?? null;
        set({ examPinPriceLoading: false, examPinPrice: payload });
        return { success: true, price: payload };
      } catch (err) {
        set({ examPinPriceLoading: false, examPinPriceError: err.message, examPinPrice: null });
        return { success: false, error: err.message };
      }
    },

    // ── PATCH /api/admin/exam-pins/price ──
    updateExamPinPrice: async ({ price, examType } = {}) => {
      set({ examPinMutationLoading: true, examPinMutationError: null });
      try {
        const { response, data } = await fetchJson(
          "/api/admin/exam-pins/price",
          {
            method: "PATCH",
            headers: authHeaders(resolveToken(get)),
            body: JSON.stringify({ price, examType }),
          }
        );
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to update exam pin price");
          set({ examPinMutationLoading: false, examPinMutationError: error });
          return { success: false, error };
        }
        set({ examPinMutationLoading: false, successMessage: "Exam pin price updated" });
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        set({ examPinMutationLoading: false, examPinMutationError: err.message });
        return { success: false, error: err.message };
      }
    },

  };
}
